interface PieChartDataPoint {
  label: string;
  value: number;
  color?: string;
}

interface PieChartProps {
  data: PieChartDataPoint[];
  title?: string;
  size?: number;
  showLegend?: boolean;
  loading?: boolean;
  /** Format the legend value (e.g. currency). Defaults to the raw value. */
  valueFormatter?: (value: number) => string;
}

export default function PieChart({
  data,
  title,
  size = 200,
  showLegend = true,
  loading = false,
  valueFormatter = (v) => `${v}`,
}: PieChartProps) {
  if (loading) {
    return (
      <div className="animate-pulse flex flex-col items-center">
        {title && <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>}
        <div className="rounded-full bg-gray-200" style={{ width: size, height: size }}></div>
        {showLegend && (
          <div className="mt-4 space-y-2 w-full">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-200 rounded-full"></div>
                <div className="h-3 bg-gray-200 rounded flex-1"></div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ width: size, height: size }}>
        <p className="text-gray-400 text-sm">No data available</p>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#8B5CF6', // purple
    '#F59E0B', // yellow
    '#EF4444', // red
    '#6366F1', // indigo
  ];

  let currentAngle = -90; // Start from top
  const segments = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    const radius = size / 2;
    const x1 = radius + radius * Math.cos((startAngle * Math.PI) / 180);
    const y1 = radius + radius * Math.sin((startAngle * Math.PI) / 180);
    const x2 = radius + radius * Math.cos((endAngle * Math.PI) / 180);
    const y2 = radius + radius * Math.sin((endAngle * Math.PI) / 180);
    const largeArc = angle > 180 ? 1 : 0;

    const path = `M ${radius},${radius} L ${x1},${y1} A ${radius},${radius} 0 ${largeArc},1 ${x2},${y2} Z`;

    return {
      path,
      color: item.color || colors[index % colors.length],
      label: item.label,
      value: item.value,
      // show "<0.1" instead so a real (small) value never reads as zero. True 0 stays "0.0".
      percentage: percentage > 0 && percentage < 0.1 ? '<0.1' : percentage.toFixed(1),
    };
  });

  return (
    <div className="flex flex-col items-center">
      {title && <h4 className="text-lg font-semibold text-gray-900 mb-4">{title}</h4>}

      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((segment, index) => (
          <path
            key={index}
            d={segment.path}
            fill={segment.color}
            className="transition-opacity hover:opacity-80 cursor-pointer"
          />
        ))}
      </svg>

      {showLegend && (
        <div className="mt-4 space-y-2 w-full">
          {segments.map((segment, index) => (
            <div key={index} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: segment.color }}
                />
                <span className="text-sm text-gray-700 truncate">{segment.label}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-sm font-medium text-gray-900">{valueFormatter(segment.value)}</span>
                <span className="text-xs text-gray-500">({segment.percentage}%)</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
